/**
 * Page blueprints — pre-composed section templates for the new-page flow.
 *
 * Each blueprint defines an ordered list of sections with their type and
 * default props. When a page is created from a blueprint, the section array
 * is serialized as the page's initial draft_content.
 *
 * Only section types registered in common/sections/registry.ts are used.
 * Props match each section's `defaults` export from its index.tsx.
 */

export interface BlueprintSection {
  type: string;
  props: Record<string, unknown>;
}

export interface Blueprint {
  id: string;
  name: string;
  description: string;
  sectionCount: string;
  sections: BlueprintSection[];
}

export const BLUEPRINTS: Blueprint[] = [
  // ── About ────────────────────────────────────────────────────────────
  {
    id: "about",
    name: "About",
    description:
      "Tell your story. Introduce your firm, your mission, and the people behind it.",
    sectionCount: "6 sections",
    sections: [
      {
        type: "Hero",
        props: {
          heading: "About Our Firm",
          subheading:
            "Dedicated to helping you achieve your goals since 2010.",
          alignment: "center",
          layout: "centered",
          imagePosition: "right",
          backgroundStyle: "primaryBg",
        },
      },
      {
        type: "TextBlock",
        props: {
          layout: "prose",
          heading: "Our Mission",
          body: "We believe every client deserves attentive, personalized guidance from experienced professionals. Our philosophy is simple: listen carefully, explain clearly, and deliver practical solutions that make a real difference. Since our founding, we have helped thousands of individuals and businesses navigate complex challenges with confidence.",
          alignment: "left",
          backgroundStyle: "surface",
        },
      },
      {
        type: "StatsBar",
        props: {
          stats: [
            { value: "15", suffix: "+", label: "Years of Experience" },
            { value: "2,500", suffix: "+", label: "Clients Served" },
            { value: "98", suffix: "%", label: "Client Satisfaction" },
            { value: "50", suffix: "+", label: "Professional Team Members" },
          ],
          backgroundStyle: "primary",
        },
      },
      {
        type: "TeamGrid",
        props: {
          heading: "Meet Our Team",
          members: [
            {
              name: "Alex Johnson",
              title: "Managing Partner",
              bio: "Over 15 years of experience helping clients navigate complex challenges with clarity and confidence.",
            },
            {
              name: "Maria Chen",
              title: "Senior Advisor",
              bio: "Specializes in strategic planning and risk management for growing businesses.",
            },
            {
              name: "David Park",
              title: "Associate",
              bio: "Focused on delivering thorough analysis and practical recommendations.",
            },
            {
              name: "Rachel Torres",
              title: "Client Relations Manager",
              bio: "Ensures every client receives responsive, personalized attention.",
            },
          ],
          layout: "grid",
          columns: 4,
          showBio: true,
          backgroundStyle: "surface",
        },
      },
      {
        type: "Testimonials",
        props: {
          heading: "What Our Clients Say",
          items: [
            {
              quote:
                "I was nervous since I had never needed legal assistance before, but the friendly demeanor put me at ease immediately. Every aspect was explained in great detail.",
              author: "Andy M.",
            },
            {
              quote:
                "There was no sales pitch, only professional assistance with helping make our choices to benefit our family long term.",
              author: "Megan M.",
            },
            {
              quote:
                "Exactly what I'd hoped to find. They took the time to understand our situation, provided astute analysis, and helped us make informed decisions.",
              author: "Bryan C.",
            },
          ],
          layout: "grid",
          backgroundStyle: "secondaryBg",
        },
      },
      {
        type: "CTABanner",
        props: {
          heading: "Want to Learn More?",
          body: "Schedule a conversation with our team.",
          ctaLabel: "Get in Touch",
          ctaHref: "/contact",
          backgroundStyle: "primary",
        },
      },
    ],
  },

  // ── Services ─────────────────────────────────────────────────────────
  {
    id: "services",
    name: "Services",
    description:
      "Showcase what you offer and how you work.",
    sectionCount: "5 sections",
    sections: [
      {
        type: "Hero",
        props: {
          heading: "Our Services",
          subheading: "Comprehensive solutions tailored to your needs.",
          alignment: "center",
          layout: "centered",
          backgroundStyle: "primaryBg",
        },
      },
      {
        type: "FeatureGrid",
        props: {
          heading: "What We Offer",
          columns: 3,
          items: [
            {
              icon: "📋",
              title: "Strategic Planning",
              body: "Develop a clear roadmap for your goals with expert guidance and thorough analysis.",
            },
            {
              icon: "🤝",
              title: "Ongoing Advisory",
              body: "Access experienced professionals whenever you need them, with flexible scheduling and transparent pricing.",
            },
            {
              icon: "📄",
              title: "Document Preparation",
              body: "Get professionally prepared documents tailored to your specific needs and circumstances.",
            },
          ],
          backgroundStyle: "surface",
        },
      },
      {
        type: "ProcessSteps",
        props: {
          heading: "How It Works",
          steps: [
            {
              title: "Schedule a Consultation",
              body: "Book a time that works for you. Choose phone or video, and tell us a bit about what you need.",
            },
            {
              title: "Meet With Your Advisor",
              body: "During your session, we will review your situation, answer your questions, and outline your options.",
            },
            {
              title: "Get a Clear Plan",
              body: "Walk away with a concrete action plan, written summary, and confidence about your next steps.",
            },
          ],
          layout: "steps",
          backgroundStyle: "secondaryBg",
        },
      },
      {
        type: "FAQ",
        props: {
          heading: "Common Questions About Our Services",
          items: [
            {
              question: "How does it work?",
              answer:
                "Book a session online, meet with a professional via phone or video, and pay only for the time you use.",
            },
            {
              question: "How much does a session cost?",
              answer:
                "Session pricing varies by type and duration. Visit our pricing page for current rates. There are no hidden fees or long-term commitments.",
            },
            {
              question: "Can I cancel or reschedule?",
              answer:
                "Yes. You can cancel or reschedule your session at any time before it starts at no charge.",
            },
            {
              question: "What should I prepare for my session?",
              answer:
                "Gather any relevant documents or notes about your situation. A brief summary of your questions or goals helps us make the most of your time together.",
            },
          ],
          backgroundStyle: "surface",
        },
      },
      {
        type: "BookingWidget",
        props: {
          heading: "Ready to Get Started?",
          body: "Schedule a consultation with one of our experienced professionals. Choose a time that works for you, and we will handle the rest.",
          ctaLabel: "Book a Consultation",
          ctaHref: "/book",
          showFeatures: true,
          features: [
            "Choose phone or video",
            "Flexible scheduling",
            "No commitment required",
          ],
          backgroundStyle: "primaryBg",
        },
      },
    ],
  },

  // ── Contact ──────────────────────────────────────────────────────────
  {
    id: "contact",
    name: "Contact",
    description:
      "Make it easy for visitors to reach you.",
    sectionCount: "3 sections",
    sections: [
      {
        type: "Hero",
        props: {
          heading: "Contact Us",
          alignment: "center",
          layout: "centered",
          backgroundStyle: "primaryBg",
        },
      },
      {
        type: "ContactCard",
        props: {
          heading: "Get in Touch",
          subheading:
            "We would love to hear from you. Reach out using any of the methods below.",
          items: [
            {
              label: "Phone",
              value: "(555) 123-4567",
              href: "tel:+15551234567",
              icon: "phone",
            },
            {
              label: "Email",
              value: "info@example.com",
              href: "mailto:info@example.com",
              icon: "mail",
            },
            {
              label: "Office Hours",
              value: "Monday – Friday, 9:00 AM – 5:00 PM",
              icon: "clock",
            },
            {
              label: "Address",
              value: "123 Main Street, Suite 200\nAnytown, ST 10001",
              icon: "map-pin",
            },
          ],
          layout: "list",
          backgroundStyle: "surface",
        },
      },
      {
        type: "CTABanner",
        props: {
          heading: "Prefer to Book Online?",
          body: "Schedule a consultation at a time that works for you.",
          ctaLabel: "Book a Consultation",
          ctaHref: "/book",
          backgroundStyle: "primary",
        },
      },
    ],
  },

  // ── FAQ ──────────────────────────────────────────────────────────────
  {
    id: "faq",
    name: "FAQ",
    description:
      "Answer common questions upfront.",
    sectionCount: "3 sections",
    sections: [
      {
        type: "Hero",
        props: {
          heading: "Frequently Asked Questions",
          alignment: "center",
          layout: "centered",
          backgroundStyle: "primaryBg",
        },
      },
      {
        type: "FAQ",
        props: {
          heading: "Frequently Asked Questions",
          items: [
            {
              question: "How does the process work?",
              answer:
                "Start by booking a session online. You will be matched with an experienced professional who will review your situation, answer your questions, and provide clear guidance.",
            },
            {
              question: "How much does a session cost?",
              answer:
                "Session pricing varies by type and duration. Visit our pricing page for current rates. There are no hidden fees or long-term commitments.",
            },
            {
              question: "Can I cancel or reschedule?",
              answer:
                "Yes. You can cancel or reschedule your session at any time before it starts at no charge.",
            },
            {
              question: "What should I prepare for my session?",
              answer:
                "Gather any relevant documents or notes about your situation. A brief summary of your questions or goals helps us make the most of your time together.",
            },
            {
              question: "Is my information kept confidential?",
              answer:
                "Absolutely. We maintain strict confidentiality for all client communications and materials.",
            },
            {
              question: "How long is a typical session?",
              answer:
                "Most sessions run 30 to 60 minutes, though the exact length depends on the complexity of your situation. You are never rushed.",
            },
            {
              question: "What happens after my session?",
              answer:
                "You will receive a written summary of the key points discussed and any recommended next steps. Follow-up sessions can be booked as needed.",
            },
            {
              question: "What are your team's qualifications?",
              answer:
                "Our professionals hold advanced degrees and certifications in their respective fields, with years of practical experience serving clients like you.",
            },
          ],
          backgroundStyle: "surface",
        },
      },
      {
        type: "CTABanner",
        props: {
          heading: "Still Have Questions?",
          body: "Our team is happy to help. Reach out anytime.",
          ctaLabel: "Contact Us",
          ctaHref: "/contact",
          backgroundStyle: "primary",
        },
      },
    ],
  },

  // ── Blank ────────────────────────────────────────────────────────────
  {
    id: "blank",
    name: "Blank Page",
    description: "Start from scratch.",
    sectionCount: "Blank",
    sections: [],
  },
];
